import { Client, ClientOptions } from 'models/flexible/Client';
import { Credential, CredentialOptions } from 'models/flexible/Credential';
import { GroupType, GroupTypeOptions } from 'models/flexible/GroupType';
import type { PERMISSIONS } from 'models/flexible/Profile';
import { UserPreference, UserPreferenceOptions } from 'models/flexible/UserPreference';
import { InsightsFilterOptions } from 'models/userPreferences/InsightsFilter';
import { NotificationOptions } from 'models/userPreferences/Notification';
import { PortfolioFilterOptions } from 'models/userPreferences/PortfolioFilter';
import UserPreferenceModelFactory from 'models/userPreferences/UserPreferenceModelFactory';
import type { Typed } from 'services/api/fields/core';

export type RoleOptions = {
  id: string;
  name: string;
  permissions?: Partial<Record<keyof typeof PERMISSIONS, boolean>>;
};

export class Role {
  id: string;
  name: string;
  permissions: Partial<Record<keyof typeof PERMISSIONS, boolean>>;

  constructor(options: Partial<RoleOptions> = {}) {
    this.id = options.id ?? '';
    this.name = options.name ?? '';
    this.permissions = options.permissions ?? {};
  }
}

export interface UserOptions {
  client?: ClientOptions | null;
  credential?: CredentialOptions | null;
  role?: RoleOptions | string | null;
  preferences?: (UserPreferenceOptions | InsightsFilterOptions | PortfolioFilterOptions | NotificationOptions)[];
  actingClient?: ClientOptions | null;
  actingClientId?: string | null;
  enabledProviderTypes?: GroupTypeOptions[];
  managedProviderTypes?: GroupTypeOptions[];
  providedProviderTypes?: GroupTypeOptions[];
  selectedProviderTypes?: GroupTypeOptions[];
  selectedManagedProviderTypes?: GroupTypeOptions[];
  selectedProvidedProviderTypes?: GroupTypeOptions[];
  name?: string;
  [key: string]: any;
}

function getDefaults(): UserOptions {
  return {
    id: '',
    active: true,
    client: null,
    credential: null,
    email: '',
    flags: [],
    groupType: null,
    locationType: null,
    name: '',
    role: null,
    permissions: {},
    preferences: [],
    lastLogin: null,
    actingClient: null,
    actingClientId: null,
    enabledProviderTypes: [],
    enabledProviderTypeNames: '',
    managedProviderTypes: [],
    providedProviderTypes: [],
    selectedProviderTypes: [],
    selectedGroups: [],
    selectedManagedProviderTypes: [],
    selectedProvidedProviderTypes: [],
    allowedGroupNames: '',
    demoPersona: null,
  } as UserOptions;
}

// Forward declare so _User's constructor can return the infused type

let User: new <T extends UserOptions>(options: T) => Typed<_User<T>, T>;

export class _User<T extends UserOptions = UserOptions> {
  client!: Client<NonNullable<T['client']>> | null;
  credential!: Credential | null;
  role!: Role | null;
  preferences!: UserPreference[];
  actingClient!: Client<NonNullable<T['actingClient']> | NonNullable<T['client']>>;
  actingClientId!: string | null;
  enabledProviderTypes!: GroupType[];
  managedProviderTypes!: GroupType[];
  providedProviderTypes!: GroupType[];
  selectedProviderTypes!: GroupType[];
  selectedManagedProviderTypes!: GroupType[];
  selectedProvidedProviderTypes!: GroupType[];
  name!: string;
  groupType!: string | null;
  flags!: string[];
  permissions!: Record<string, boolean>;

  static getDefaults = getDefaults;

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.client = opts.client ? new Client<NonNullable<T['client']>>(opts.client) : null;
    this.credential = opts.credential ? new Credential(opts.credential) : null;
    this.role = opts.role && typeof opts.role === 'object' ? new Role(opts.role) : null;
    this.preferences = (opts.preferences ?? []).map((pref) => UserPreferenceModelFactory.get(pref));
    this.actingClient = opts.actingClient?.id
      ? new Client<NonNullable<T['actingClient']> | NonNullable<T['client']>>(opts.actingClient!)
      : new Client<NonNullable<T['actingClient']> | NonNullable<T['client']>>(opts.client!);
    this.actingClientId = opts.actingClientId || this.actingClient.id;
    this.enabledProviderTypes = (opts.enabledProviderTypes ?? [])
      .map((providerType) => new GroupType(providerType))
      .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
    this.managedProviderTypes = (opts.managedProviderTypes ?? []).map((providerType) => new GroupType(providerType));
    this.providedProviderTypes = (opts.providedProviderTypes ?? []).map((providerType) => new GroupType(providerType));
    this.selectedProviderTypes = (opts.selectedProviderTypes ?? []).map((providerType) => new GroupType(providerType));
    this.selectedManagedProviderTypes = (opts.selectedManagedProviderTypes ?? []).map(
      (providerType) => new GroupType(providerType)
    );
    this.selectedProvidedProviderTypes = (opts.selectedProvidedProviderTypes ?? []).map(
      (providerType) => new GroupType(providerType)
    );
  }

  get fullName() {
    const { credential } = this;

    if (credential?.title) {
      return `${this.name}, ${credential.title}`;
    }

    return this.name;
  }
}

// eslint-disable-next-line prefer-const
User = _User as typeof User;

export default User;
export { User };
export type User<T extends UserOptions = UserOptions> = _User<T> & Omit<T, keyof _User<T>>;
